import type { MerchandiseShippingOrderCreationItem } from 'api/order'
import getMerchandiseShippingItemViews from 'checkout/selectors/view/itemViews/getMerchandiseShippingItemViews'
import getMerchandiseItems from 'checkout/selectors/view/items/getMerchandiseItems'
import { createSelector } from 'reselect'
import { getAllPromoCodeItemDiscounts } from '../checkout'
import type { PromoItemDiscountsWithStatus } from 'lib/promo/promoMappers'
import { filterItemDiscounts } from 'lib/promo/promoMappers'
import formatItemDiscount from 'lib/payment/formatDiscountItem'

function isDeliveryFormComplete(form: App.Checkout.DeliveryForm): boolean {
  return !!(
    form.phoneNumber &&
    form.firstName &&
    form.lastName &&
    form.address &&
    form.city &&
    form.state &&
    form.postcode
  )
}

const getFormattedMerchandiseShippingItems = createSelector(
  (state: App.State) => getMerchandiseShippingItemViews(state),
  (state: App.State) => getMerchandiseItems(state),
  (state: App.State) => state.checkout.form.deliveryForm,
  (state: App.State) => getAllPromoCodeItemDiscounts(state),
  (views, merchandiseItems, form, itemDiscounts: PromoItemDiscountsWithStatus): App.WithDataStatus<Array<MerchandiseShippingOrderCreationItem>> => {
    // If no merchandise items in cart, no shipping required
    if (merchandiseItems.length === 0) {
      return { hasRequiredData: true, data: [] }
    }

    // If delivery form is incomplete, block order creation
    if (!isDeliveryFormComplete(form)) {
      return { hasRequiredData: false, data: [] }
    }

    // If no shipping items exist (API not ready or edge case), block order creation
    if (views.data.length === 0) {
      return { hasRequiredData: false, data: [] }
    }

    const items = views.data.map((view): MerchandiseShippingOrderCreationItem => {
      const thisItemDiscounts = filterItemDiscounts({
        itemDiscounts: itemDiscounts.itemDiscounts,
        lookup: {
          specificType: 'cart-id-based',
          cartItemIds: [view.item.itemId ?? ''],
        },
      })

      return {
        type: 'merchandise_shipping',
        transaction_key: view.item.transactionKey,
        total: view.totals.otherFees?.deliveryFee ?? 0,
        shippingGst: view.totals.otherFees?.deliveryGst,
        shippingGroup: view.item.itemId,
        itemDiscounts: thisItemDiscounts.map(d => formatItemDiscount(d, view.item.transactionKey)),
        address: {
          firstName: form.firstName!,
          lastName: form.lastName!,
          company: form.company,
          country: form.country ?? 'Australia',
          phone: `+${form.phoneNumber!.prefix}${form.phoneNumber!.phone}`,
          address: form.address!,
          address2: form.unitNumber ?? undefined,
          city: form.city!,
          state: form.state!,
          postcode: form.postcode!,
        },
      }
    })

    return {
      hasRequiredData: true,
      data: items,
    }
  },
)

export default getFormattedMerchandiseShippingItems
